import axios from 'axios';

// Use relative URL for production (same domain), absolute URL for development
const isProduction = typeof window !== 'undefined' && !window.location.hostname.includes('localhost');
const BACKEND_URL = isProduction ? '' : (process.env.REACT_APP_BACKEND_URL || '');
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('onirva_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect to login for auth endpoints (login, register, password reset)
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('onirva_token');
      localStorage.removeItem('onirva_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me');
export const logout = () => api.post('/auth/logout');
export const verifySecurityQuestion = (data) => api.post('/auth/verify-security-question', data);
export const resetPassword = (data) => api.post('/auth/reset-password', data);
export const requestEmailReset = (email, originUrl) => api.post('/auth/request-email-reset', { email, origin_url: originUrl });

// Session endpoint needs credentials for cookies (Google OAuth)
export const processSession = (sessionId) => api.post('/auth/session', { session_id: sessionId }, { withCredentials: true });

// Profile
export const getProfile = () => api.get('/profile');
export const updateProfile = (data) => api.put('/profile', data);
export const completeOnboarding = () => api.post('/profile/complete-onboarding');

// Dreams
export const createDream = (data) => api.post('/dreams', data);
export const getDreams = (limit = 50, skip = 0) => api.get(`/dreams?limit=${limit}&skip=${skip}`);
export const getDream = (dreamId) => api.get(`/dreams/${dreamId}`);
export const deleteDream = (dreamId) => api.delete(`/dreams/${dreamId}`);
export const analyzeDream = (dreamId) => api.post(`/dreams/${dreamId}/analyze`);
export const getDreamContext = (dreamId) => api.get(`/dreams/${dreamId}/context`);
export const continueDreamConversation = (dreamId, message) => api.post(`/dreams/${dreamId}/conversation`, { message });
export const getDreamConversation = (dreamId) => api.get(`/dreams/${dreamId}/conversation`);

// Transcription
export const transcribeAudio = async (audioBlob) => {
  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.webm');
  return api.post('/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Stats
export const getStats = () => api.get('/stats');

// Subscription
export const getSubscription = () => api.get('/subscription');
export const updateSubscription = (data) => api.post('/subscription/update', data);
export const cancelSubscription = () => api.post('/subscription/cancel');

// Analytics & Reports
export const getMonthlyAnalytics = (month, year) => {
  const params = new URLSearchParams();
  if (month) params.append('month', month);
  if (year) params.append('year', year);
  return api.get(`/analytics/monthly?${params.toString()}`);
};
export const generateMonthlySummary = (month, year) => {
  const params = new URLSearchParams();
  if (month) params.append('month', month);
  if (year) params.append('year', year);
  return api.post(`/analytics/generate-summary?${params.toString()}`);
};
export const downloadPdfReport = async (month, year) => {
  const params = new URLSearchParams();
  if (month) params.append('month', month);
  if (year) params.append('year', year);
  const response = await api.get(`/analytics/report/pdf?${params.toString()}`, {
    responseType: 'blob'
  });
  return response;
};

// Dream Intelligence Features
export const getIntelligenceOverview = () => api.get('/intelligence/overview');

// Symbol Dictionary
export const getSymbolDictionary = () => api.get('/intelligence/symbols');
export const getSymbolDetail = (symbol) => api.get(`/intelligence/symbols/${encodeURIComponent(symbol)}`);
export const generateSymbolInsight = (symbol) => api.post(`/intelligence/symbols/${encodeURIComponent(symbol)}/generate-insight`);

// Dream Climate
export const getDreamClimate = (period = 'month') => api.get(`/intelligence/climate?period=${period}`);
export const generateClimateReflection = (period = 'month') => api.post(`/intelligence/climate/generate-reflection?period=${period}`);

// Growth Threads
export const getGrowthThreads = () => api.get('/intelligence/threads');
export const discoverGrowthThreads = () => api.post('/intelligence/threads/discover');

// Dream Evolution
export const getDreamEvolution = () => api.get('/intelligence/evolution');
export const analyzeDreamEvolution = () => api.post('/intelligence/evolution/analyze');

// Payments
export const getSubscriptionPackages = () => api.get('/payments/packages');
export const createCheckoutSession = (packageId, originUrl) => api.post('/payments/checkout/create', { package_id: packageId, origin_url: originUrl });
export const getCheckoutStatus = (sessionId) => api.get(`/payments/checkout/status/${sessionId}`);
export const getPaymentHistory = () => api.get('/payments/history');

export default api;
